import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Close as CloseIcon,
  Download as DownloadIcon,
} from "@mui/icons-material";
import { Box, Button, Chip, IconButton, Modal, Stack, Typography } from "@mui/material";
import { useCallback, useEffect, useRef, useState, type FC } from "react";

import { useStorageUrlWithCredentials } from "../components/imagesPreview/storageUrlWithCredentials";
import type { MediaImage } from "../media/mediaApi";

const SWIPE_THRESHOLD_PX = 48;

interface Props {
  image: MediaImage | null;
  images: MediaImage[];
  onClose: () => void;
  onDownload: (type: "original" | "www" | "thumbnail", image: MediaImage) => void;
  onNavigate: (image: MediaImage) => void;
}

const MediaLightbox: FC<Props> = ({ image, images, onClose, onDownload, onNavigate }) => {
  const currentIndex = image ? images.findIndex((item) => item.id === image.id) : -1;
  const hasPrev = currentIndex > 0;
  const hasNext = currentIndex >= 0 && currentIndex < images.length - 1;
  const imageUrl = useStorageUrlWithCredentials(image?.original.url);
  const pointerStartRef = useRef<{ x: number; y: number }>(null);
  const activePointerIdRef = useRef<number | null>(null);
  const [dragX, setDragX] = useState(0);
  const [isDragging, setIsDragging] = useState(false);

  const goPrev = useCallback(() => {
    if (hasPrev) onNavigate(images[currentIndex - 1]);
  }, [hasPrev, onNavigate, images, currentIndex]);

  const goNext = useCallback(() => {
    if (hasNext) onNavigate(images[currentIndex + 1]);
  }, [hasNext, onNavigate, images, currentIndex]);

  const resetSwipe = useCallback(() => {
    pointerStartRef.current = null;
    activePointerIdRef.current = null;
    setDragX(0);
    setIsDragging(false);
  }, []);

  useEffect(() => {
    resetSwipe();
  }, [image?.id, resetSwipe]);

  const applyDragOffset = useCallback(
    (deltaX: number, deltaY: number) => {
      if (Math.abs(deltaX) <= Math.abs(deltaY) && Math.abs(deltaX) < 8) return;

      let offset = deltaX;
      if (!hasPrev && offset > 0) offset *= 0.35;
      if (!hasNext && offset < 0) offset *= 0.35;
      setDragX(offset);
    },
    [hasPrev, hasNext],
  );

  const commitSwipe = useCallback(
    (deltaX: number, deltaY: number) => {
      if (Math.abs(deltaX) >= SWIPE_THRESHOLD_PX && Math.abs(deltaX) > Math.abs(deltaY)) {
        if (deltaX < 0) goNext();
        else goPrev();
      }
    },
    [goNext, goPrev],
  );

  const handlePointerDown = (event: React.PointerEvent) => {
    if (event.pointerType === "mouse" && event.button !== 0) return;
    if (event.target instanceof Element && event.target.closest("button, a")) {
      return;
    }

    pointerStartRef.current = { x: event.clientX, y: event.clientY };
    activePointerIdRef.current = event.pointerId;
    setIsDragging(true);
    setDragX(0);
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const handlePointerMove = (event: React.PointerEvent) => {
    if (activePointerIdRef.current !== event.pointerId) return;

    const start = pointerStartRef.current;
    if (!start) return;

    applyDragOffset(event.clientX - start.x, event.clientY - start.y);
  };

  const handlePointerUp = (event: React.PointerEvent) => {
    if (activePointerIdRef.current !== event.pointerId) return;

    const start = pointerStartRef.current;
    if (!start) {
      resetSwipe();
      return;
    }

    const deltaX = event.clientX - start.x;
    const deltaY = event.clientY - start.y;
    const didSwipe = Math.abs(deltaX) >= SWIPE_THRESHOLD_PX && Math.abs(deltaX) > Math.abs(deltaY);

    resetSwipe();
    commitSwipe(deltaX, deltaY);

    if (didSwipe) {
      event.preventDefault();
      event.stopPropagation();
    }
  };

  const handlePointerCancel = (event: React.PointerEvent) => {
    if (activePointerIdRef.current === event.pointerId) resetSwipe();
  };

  useEffect(() => {
    if (!image) return undefined;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "ArrowLeft") {
        event.preventDefault();
        goPrev();
      }
      if (event.key === "ArrowRight") {
        event.preventDefault();
        goNext();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [image, goPrev, goNext]);

  if (!image) return null;

  const navButtonSx = {
    position: "fixed",
    top: "50%",
    transform: "translateY(-50%)",
    color: "#fff",
    bgcolor: "rgba(255,255,255,0.12)",
    "&:hover": { bgcolor: "rgba(255,255,255,0.22)" },
    "&.Mui-disabled": { color: "rgba(255,255,255,0.25)", bgcolor: "rgba(255,255,255,0.06)" },
  };

  return (
    <Modal open={Boolean(image)} onClose={onClose} sx={{ zIndex: 1400 }}>
      <Box
        onClick={onClose}
        sx={{
          position: "fixed",
          inset: 0,
          bgcolor: "rgba(0, 0, 0, 0.88)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: { xs: 2, md: 4 },
        }}
      >
        {" "}
        <IconButton
          onClick={onClose}
          aria-label="Zamknij podgląd"
          sx={{
            position: "fixed",
            top: 16,
            right: 16,
            color: "#fff",
            bgcolor: "rgba(255,255,255,0.12)",
            "&:hover": { bgcolor: "rgba(255,255,255,0.22)" },
          }}
        >
          <CloseIcon />
        </IconButton>
        <IconButton
          onClick={(event) => {
            event.stopPropagation();
            goPrev();
          }}
          disabled={!hasPrev}
          aria-label="Poprzednie zdjęcie"
          sx={{ ...navButtonSx, left: { xs: 8, md: 24 } }}
        >
          <ChevronLeftIcon fontSize="large" />
        </IconButton>
        <IconButton
          onClick={(event) => {
            event.stopPropagation();
            goNext();
          }}
          disabled={!hasNext}
          aria-label="Następne zdjęcie"
          sx={{ ...navButtonSx, right: { xs: 8, md: 24 } }}
        >
          <ChevronRightIcon fontSize="large" />
        </IconButton>
        <Box
          onClick={(event) => event.stopPropagation()}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerCancel}
          sx={{
            maxWidth: "min(960px, 92vw)",
            width: "100%",
            outline: "none",
            touchAction: "none",
            userSelect: "none",
            WebkitUserSelect: "none",
            WebkitTouchCallout: "none",
            cursor: isDragging ? "grabbing" : "grab",
          }}
        >
          <Box
            component="img"
            src={imageUrl}
            alt={image.name}
            draggable={false}
            sx={{
              width: "100%",
              maxHeight: "72vh",
              objectFit: "contain",
              borderRadius: 2,
              display: "block",
              boxShadow: "0 24px 64px rgba(0,0,0,0.45)",
              transform: dragX ? `translateX(${dragX}px)` : "none",
              transition: isDragging ? "none" : "transform 0.22s ease-out",
              opacity: dragX ? 1 - Math.min(Math.abs(dragX) / 420, 0.12) : 1,
            }}
          />
          <Box sx={{ mt: 2, color: "#fff" }}>
            <Stack
              direction="row"
              spacing={2}
              sx={{ mb: 0.5, justifyContent: "space-between", alignItems: "baseline" }}
            >
              <Typography id="media-lightbox-title" variant="h6" sx={{ fontWeight: "bold" }}>
                {image.name}
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.7, flexShrink: 0 }}>
                {currentIndex + 1} / {images.length}
              </Typography>
            </Stack>
            <Typography variant="body2" sx={{ opacity: 0.85, mb: 1.5 }}>
              {image.info}
            </Typography>
            <Stack direction="row" spacing={0.75} useFlexGap sx={{ mb: 2, flexWrap: "wrap" }}>
              {image.tags.map((tag) => (
                <Chip
                  key={tag}
                  label={tag}
                  size="small"
                  sx={{
                    bgcolor: "rgba(255,255,255,0.15)",
                    color: "#fff",
                    border: "1px solid rgba(255,255,255,0.2)",
                  }}
                />
              ))}
            </Stack>
            <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: "wrap" }}>
              <Button
                variant="contained"
                size="small"
                startIcon={<DownloadIcon />}
                onClick={() => onDownload("www", image)}
                sx={{
                  bgcolor: "#fff",
                  color: "primary.main",
                  "&:hover": { bgcolor: "rgba(255,255,255,0.9)" },
                }}
              >
                Pobierz (web)
              </Button>
              <Button
                variant="outlined"
                size="small"
                startIcon={<DownloadIcon />}
                onClick={() => onDownload("original", image)}
                sx={{
                  color: "#fff",
                  borderColor: "rgba(255,255,255,0.5)",
                  "&:hover": { borderColor: "#fff", bgcolor: "rgba(255,255,255,0.08)" },
                }}
              >
                Pobierz oryginał
              </Button>
            </Stack>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

export default MediaLightbox;
